/**
 * Pure helpers of permit equipment and the generation gates (docs/features/permit-plan-sets.md §11): where each gap
 * is fixed, the "Generate with open items" confirmation's text, the history's open-items label, the Equipment step's typeahead (roles, kinds, source badges, keyboard
 * moves) and the company-equipment form (the fields each kind requires, as the server checks them). No React; tested
 * in permitEquipment.test.ts.
 * Text comes from the `permit` catalogs: labels are keys, helpers that word something take the caller's `t`.
 */
import type { MessageKey } from "../i18n/keys";
import type { Tr } from "./permits.ts";

export type EquipmentKind = "module" | "microinverter" | "inverter" | "optimizer" | "battery";
export type EquipmentRole = "module" | "inverter" | "optimizer";
export type EquipmentSourceName = "shipped" | "admin" | "cec" | "company";

/** A gap: a problem a FINAL plan set must not have (the server marks it `blocking`). */
export interface GapLike {
  id: string;
  status: string;
  blocking?: boolean | null;
}

export function isGap(c: GapLike): boolean {
  return c.status === "problem" && !!c.blocking;
}

export function gapsOf<T extends GapLike>(checks: T[]): T[] {
  return checks.filter(isGap);
}

/** Where a gap is fixed (the step and the anchor Locate flashes); null for other checks. */
export function locateGap(id: string): { step: "equipment" | "site" | "structure"; anchor: string } | null {
  if (id === "gate_module") return { step: "equipment", anchor: "module" };
  if (id === "gate_inverter") return { step: "equipment", anchor: "inverter" };
  if (id === "gate_optimizer") return { step: "equipment", anchor: "optimizer" };
  if (id === "gate_busbar") return { step: "site", anchor: "service" };
  if (id === "gate_site_locations") return { step: "site", anchor: "markers" };
  if (id === "gate_loads") return { step: "structure", anchor: "loads" };
  const face = /^gate_framing_(.+)$/.exec(id);
  if (face) return { step: "structure", anchor: `face:${face[1]}` };
  return null;
}

/** "3 required items missing" */
export function gapsText(t: Tr, n: number): string {
  return t("permit.gaps.missing", { count: n });
}

/** The "Generate with open items" confirmation: what it says and what the button is called. A paid set is never
 * stamped: each open item (and every value that depends on it) prints as a fill-in blank. `costLabel` is the
 * formatted price ("5 Lux") or null when regenerating is free. */
export function openItemsConfirm(t: Tr, gaps: number, costLabel: string | null): { title: string; body: string; price: string; confirm: string; ack: string } {
  return {
    title: t("permit.openItems.title"),
    body: t("permit.openItems.body", { count: gaps }),
    price: costLabel ? t("permit.openItems.pricePaid") : t("permit.openItems.priceFree"),
    confirm: costLabel ? t("permit.openItems.confirmCost", { cost: costLabel }) : t("permit.openItems.confirm"),
    ack: t("permit.openItems.ack"),
  };
}

/** The history's label of a plan set with open items: "Open items: 3" (null without any). */
export function openItemsLabel(t: Tr, n: number | null | undefined): string | null {
  return n ? t("permit.openItems.label", { count: n }) : null;
}

// ------------------------------------------------------------------------------------------- typeahead
export const ROLE_KINDS: Record<EquipmentRole, EquipmentKind[]> = {
  module: ["module"],
  inverter: ["inverter", "microinverter"],
  optimizer: ["optimizer"],
};

export const ROLE_LABEL: Record<EquipmentRole, MessageKey> = {
  module: "permit.equipment.role.module",
  inverter: "permit.equipment.role.inverter",
  optimizer: "permit.equipment.role.optimizer",
};

export const KIND_LABEL: Record<EquipmentKind, MessageKey> = {
  module: "permit.equipment.kind.module",
  microinverter: "permit.equipment.kind.microinverter",
  inverter: "permit.equipment.kind.inverter",
  optimizer: "permit.equipment.kind.optimizer",
  battery: "permit.equipment.kind.battery",
};

export interface EquipmentLike {
  id: string;
  kind: EquipmentKind;
  manufacturer: string;
  model: string;
  source: EquipmentSourceName;
  cec_id?: string | null;
  discontinued_at?: string | null;
  generic?: boolean;
  verified?: boolean;
  specs?: Record<string, unknown>;
}

/** The badges next to an item: where it comes from and whether the CEC lists it (catalog keys). */
export function sourceBadges(e: EquipmentLike): { label: MessageKey; tone: "info" | "ok" | "warn" | "danger" | undefined; title: MessageKey }[] {
  const out: { label: MessageKey; tone: "info" | "ok" | "warn" | "danger" | undefined; title: MessageKey }[] = [];
  if (e.source === "company") out.push({ label: "permit.equipment.badge.company", tone: "info", title: "permit.equipment.badge.companyHint" });
  if (e.cec_id) out.push({ label: "permit.equipment.badge.cec", tone: "ok", title: "permit.equipment.badge.cecHint" });
  if ((e.source === "shipped" || e.source === "admin") && !e.generic) out.push({ label: "permit.equipment.badge.catalogue", tone: undefined, title: "permit.equipment.badge.catalogueHint" });
  if (e.discontinued_at) out.push({ label: "permit.equipment.badge.discontinued", tone: "danger", title: "permit.equipment.badge.discontinuedHint" });
  if (e.generic) out.push({ label: "permit.equipment.badge.placeholder", tone: "warn", title: "permit.equipment.badge.placeholderHint" });
  return out;
}

/** One line of key values for an option ("430 W · Voc 39.3 V", "7.6 kW AC · 240 V"); numbers in the reader's locale
 * through `num` (as formatLength). */
export function specLine(t: Tr, e: EquipmentLike, num: (value: number, digits: number) => string = (v, d) => v.toFixed(d)): string {
  const s = (e.specs ?? {}) as Record<string, number | undefined>;
  const n = (v: number | undefined, d = 1) => (v == null ? null : Number.isInteger(v) ? num(v, 0) : num(v, d));
  const parts: (string | null)[] = [];
  if (e.kind === "module") parts.push(s.pmax_w != null ? `${n(s.pmax_w, 0)} W` : null, s.voc_v != null ? `Voc ${n(s.voc_v)} V` : null, s.isc_a != null ? `Isc ${n(s.isc_a, 2)} A` : null);
  else if (e.kind === "inverter") parts.push(s.ac_rated_w != null ? `${n(s.ac_rated_w / 1000, 1)} kW AC` : null, s.ac_voltage_v != null ? `${n(s.ac_voltage_v, 0)} V` : null, (e.specs as Record<string, unknown> | undefined)?.optimizer_string ? t("permit.equipment.spec.withOptimizers") : null);
  else if (e.kind === "microinverter") parts.push(s.ac_rated_w != null ? `${n(s.ac_rated_w, 0)} VA` : null, s.ac_voltage_v != null ? `${n(s.ac_voltage_v, 0)} V` : null, s.max_units_per_branch != null ? t("permit.equipment.spec.perBranch", { count: s.max_units_per_branch }) : null);
  else if (e.kind === "optimizer") parts.push(s.rated_w != null ? `${n(s.rated_w, 0)} W` : null, s.max_input_voltage_v != null ? t("permit.equipment.spec.voltsIn", { volts: n(s.max_input_voltage_v, 0)! }) : null);
  else parts.push(s.usable_kwh != null ? `${n(s.usable_kwh)} kWh` : null);
  return parts.filter(Boolean).join(" · ");
}

/** What the picker shows for a role: the chosen item, or nothing when the server sized the preview with a
 * placeholder (the installer must choose; no silent generic fallback). */
export function chosenId(role: EquipmentRole, inputs: Partial<Record<`${EquipmentRole}_id`, string | null | undefined>>, resolved: Record<string, unknown> | undefined): string {
  const own = inputs[`${role}_id`];
  if (own) return own;
  const source = (resolved?.equipment_source as Record<string, string> | undefined)?.[role];
  if (!source || source === "placeholder") return "";
  return (resolved?.[`${role}_id`] as string | undefined) ?? "";
}

/** The highlighted option after a key press in the typeahead (wraps; -1 = none). */
export function moveActive(active: number, count: number, key: string): number {
  if (!count) return -1;
  if (key === "ArrowDown") return active < 0 ? 0 : (active + 1) % count;
  if (key === "ArrowUp") return active <= 0 ? count - 1 : active - 1;
  if (key === "Home") return 0;
  if (key === "End") return count - 1;
  return active;
}

// ------------------------------------------------------------------------------------- company equipment
export interface SpecField {
  key: string;
  /** Catalog key of the field's name ("Pmax", "Rated AC power"). */
  label: MessageKey;
  unit?: string;
  required: boolean;
  kind?: "number" | "integer" | "list" | "select";
  options?: { value: string; label: MessageKey }[];
  hint?: MessageKey;
}

const COMMON_INVERTER: SpecField[] = [
  { key: "ac_rated_w", label: "permit.equipment.field.acRatedW", unit: "W", required: true },
  { key: "ac_voltage_v", label: "permit.equipment.field.acVoltageV", unit: "V", required: true },
  { key: "ac_max_current_a", label: "permit.equipment.field.acMaxCurrentA", unit: "A", required: true },
  { key: "max_dc_voltage_v", label: "permit.equipment.field.maxDcVoltageV", unit: "V", required: true },
];

/** The fields of the company-equipment form per kind; `required` mirrors the server (equipment.COMPANY_REQUIRED). */
export const SPEC_FIELDS: Record<EquipmentKind, SpecField[]> = {
  module: [
    { key: "pmax_w", label: "permit.equipment.field.pmaxW", unit: "W", required: true },
    { key: "voc_v", label: "permit.equipment.field.vocV", unit: "V", required: true },
    { key: "isc_a", label: "permit.equipment.field.iscA", unit: "A", required: true },
    { key: "vmp_v", label: "permit.equipment.field.vmpV", unit: "V", required: true },
    { key: "imp_a", label: "permit.equipment.field.impA", unit: "A", required: true },
    { key: "tc_voc_pct_per_c", label: "permit.equipment.field.tcVocPctPerC", unit: "%/°C", required: true, hint: "permit.equipment.field.tcVocPctPerCHint" },
    { key: "tc_pmax_pct_per_c", label: "permit.equipment.field.tcPmaxPctPerC", unit: "%/°C", required: false },
    { key: "max_system_voltage_v", label: "permit.equipment.field.maxSystemVoltageV", unit: "V", required: true },
    { key: "max_series_fuse_a", label: "permit.equipment.field.maxSeriesFuseA", unit: "A", required: false },
    { key: "width_m", label: "permit.equipment.field.widthM", unit: "m", required: false },
    { key: "height_m", label: "permit.equipment.field.heightM", unit: "m", required: false },
    { key: "weight_kg", label: "permit.equipment.field.weightKg", unit: "kg", required: false, hint: "permit.equipment.field.weightKgHint" },
  ],
  inverter: [
    ...COMMON_INVERTER,
    { key: "mppt_count", label: "permit.equipment.field.mpptCount", required: true, kind: "integer" },
    { key: "mppt_min_v", label: "permit.equipment.field.mpptMinV", unit: "V", required: true },
    { key: "mppt_max_v", label: "permit.equipment.field.mpptMaxV", unit: "V", required: true },
    { key: "max_isc_a", label: "permit.equipment.field.maxIscA", unit: "A", required: true, kind: "list", hint: "permit.equipment.field.maxIscAHint" },
    { key: "max_input_current_a", label: "permit.equipment.field.maxInputCurrentA", unit: "A", required: false, kind: "list" },
    { key: "start_voltage_v", label: "permit.equipment.field.startVoltageV", unit: "V", required: false },
    { key: "rapid_shutdown", label: "permit.equipment.field.rapidShutdown", required: false, kind: "select", options: [{ value: "none", label: "permit.equipment.option.rapidShutdownNone" }, { value: "mlpe", label: "permit.equipment.option.rapidShutdownMlpe" }] },
  ],
  microinverter: [
    ...COMMON_INVERTER,
    { key: "mppt_min_v", label: "permit.equipment.field.mpptMinV", unit: "V", required: true },
    { key: "mppt_max_v", label: "permit.equipment.field.mpptMaxV", unit: "V", required: true },
    { key: "max_units_per_branch", label: "permit.equipment.field.maxUnitsPerBranch", required: false, kind: "integer" },
    { key: "modules_per_unit", label: "permit.equipment.field.modulesPerUnit", required: false, kind: "integer" },
    { key: "max_module_isc_a", label: "permit.equipment.field.maxModuleIscA", unit: "A", required: false },
  ],
  optimizer: [
    { key: "rated_w", label: "permit.equipment.field.ratedW", unit: "W", required: true },
    { key: "max_input_voltage_v", label: "permit.equipment.field.maxInputVoltageV", unit: "V", required: true },
    { key: "max_input_isc_a", label: "permit.equipment.field.maxInputIscA", unit: "A", required: true },
    { key: "max_output_current_a", label: "permit.equipment.field.maxOutputCurrentA", unit: "A", required: true },
  ],
  battery: [
    { key: "usable_kwh", label: "permit.equipment.field.usableKwh", unit: "kWh", required: true },
    { key: "max_continuous_power_kw", label: "permit.equipment.field.maxContinuousPowerKw", unit: "kW", required: true },
    { key: "nominal_voltage_v", label: "permit.equipment.field.nominalVoltageV", unit: "V", required: true },
    { key: "nameplate_kwh", label: "permit.equipment.field.nameplateKwh", unit: "kWh", required: false },
    { key: "coupling", label: "permit.equipment.field.coupling", required: false, kind: "select", options: [{ value: "ac", label: "permit.equipment.option.acCoupled" }, { value: "dc", label: "permit.equipment.option.dcCoupled" }] },
  ],
};

export type SpecValues = Record<string, string>;

/** The form's values as the API's `specs` and the first problem, if any (the server checks again). */
export function specsFromForm(t: Tr, kind: EquipmentKind, values: SpecValues): { specs: Record<string, unknown>; error: string | null } {
  const specs: Record<string, unknown> = {};
  for (const f of SPEC_FIELDS[kind]) {
    const raw = (values[f.key] ?? "").trim().replace(/−/g, "-");
    if (!raw) {
      if (f.required) return { specs, error: t("permit.equipment.error.required", { field: t(f.label) }) };
      continue;
    }
    if (f.kind === "select") {
      specs[f.key] = raw;
      continue;
    }
    if (f.kind === "list") {
      const list = raw.split(/[,;\s]+/).filter(Boolean).map(Number);
      if (list.some((v) => !Number.isFinite(v) || v <= 0)) return { specs, error: t("permit.equipment.error.list", { field: t(f.label) }) };
      specs[f.key] = list;
      continue;
    }
    const v = Number(raw);
    if (!Number.isFinite(v)) return { specs, error: t("permit.equipment.error.number", { field: t(f.label) }) };
    if (f.kind === "integer" && !Number.isInteger(v)) return { specs, error: t("permit.equipment.error.integer", { field: t(f.label) }) };
    specs[f.key] = v;
  }
  if (kind === "module") {
    const s = specs as Record<string, number>;
    if (s.vmp_v! >= s.voc_v!) return { specs, error: t("permit.equipment.error.vmpVoc") };
    if (s.imp_a! >= s.isc_a!) return { specs, error: t("permit.equipment.error.impIsc") };
    if (!(s.tc_voc_pct_per_c! < 0 && s.tc_voc_pct_per_c! > -1)) return { specs, error: t("permit.equipment.error.vocCoefficient") };
  }
  if ((kind === "inverter" || kind === "microinverter") && Number(specs.mppt_min_v) >= Number(specs.mppt_max_v)) return { specs, error: t("permit.equipment.error.mppt") };
  if (kind === "inverter" && Array.isArray(specs.max_isc_a) && specs.mppt_count != null && (specs.max_isc_a as number[]).length > Number(specs.mppt_count)) return { specs, error: t("permit.equipment.error.iscCount") };
  return { specs, error: null };
}

/** The kinds a role's "Add your own" offers (an inverter picker adds a string inverter or a microinverter). */
export function kindsForRole(role: EquipmentRole): EquipmentKind[] {
  return ROLE_KINDS[role];
}
